import { useState } from 'react';

const theme = {
  colors: {
    primary: '#003462',
    primaryLight: '#164975',
    primaryDark: '#002345',
    secondary: '#02b1b2',
    secondaryLight: '#1ac0c1',
    secondaryDark: '#019393',
    background: '#f5f7f9',
    paper: '#ffffff',
    textPrimary: '#003462',
    textSecondary: '#4a5568',
    error: '#d32f2f',
    warning: '#ed6c02',
    success: '#2e7d32',
  }
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 16px',
  },
  card: {
    backgroundColor: theme.colors.paper,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  cardContent: {
    padding: '24px',
  },
  button: {
    backgroundColor: theme.colors.secondary,
    color: 'white',
    border: '2px solid transparent',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '16px',
    marginBottom: '16px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  },
  alert: {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '2px solid',
  },
  alertError: {
    backgroundColor: '#ffebee',
    color: theme.colors.error,
    borderColor: theme.colors.error,
  },
  alertWarning: {
    backgroundColor: '#fff8e1',
    color: theme.colors.warning,
    borderColor: theme.colors.warning,
  },
  progressBar: {
    width: '100%',
    height: '10px',
    backgroundColor: 'rgba(2, 177, 178, 0.1)',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid rgba(2, 177, 178, 0.2)',
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.colors.secondary,
    transition: 'width 0.3s ease',
  },
  chip: {
    display: 'inline-block',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    margin: '4px',
    border: '2px solid transparent',
  },
  grid: {
    display: 'grid',
    gap: '24px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  },
  twoColumnGrid: {
    display: 'grid',
    gap: '24px',
    gridTemplateColumns: 'repeat(2, 1fr)',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    }
  },
  header: {
    backgroundColor: theme.colors.paper,
    borderBottom: '3px solid #e2e8f0',
    padding: '16px 0',
    marginBottom: '32px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    height: '40px',
    width: 'auto',
  },
  loading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #333',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  }
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError(null);
    setLoading(true);
    
    // Debug: Log the environment variable and full URL
    console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    const fullUrl = `${process.env.REACT_APP_API_URL}/auth/login/`;
    console.log('Full URL being called:', fullUrl);
    
    try {
      const res = await fetch(fullUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      console.log('Response status:', res.status);
      console.log('Response URL:', res.url);
      
      const data = await res.json();
      if (res.ok) {
        onLogin(data.access_token);
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: theme.colors.background, 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '400px', width: '100%' }}>
        <div style={styles.card}>
          <div style={{ ...styles.cardContent, textAlign: 'center' }}>
            <h1 style={{ 
              color: theme.colors.primary, 
              marginBottom: '8px',
              fontSize: '32px',
              fontWeight: '600'
            }}>
              Welcome to Sentiyo
            </h1>
            <p style={{ 
              color: theme.colors.textSecondary, 
              marginBottom: '32px',
              fontSize: '16px'
            }}>
              Login to continue to your dashboard
            </p>
            
            {error && (
              <div style={{ ...styles.alert, ...styles.alertError }}>
                {error}
              </div>
            )}
            
            <div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                required
              />
              <button
                onClick={handleLogin}
                disabled={loading}
                style={{
                  ...styles.button,
                  width: '100%',
                  ...(loading ? styles.buttonDisabled : {})
                }}
              >
                {loading ? (
                  <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    Logging in...
                  </div>
                ) : 'Login'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Upload = ({ token, onLogout }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setResponse(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Please select a file before submitting.");
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/analyze-call/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setResponse(data);
      } else {
        setError(data.detail || "An error occurred while processing the audio file.");
      }
    } catch (err) {
      setError("An error occurred while processing the audio file.");
    } finally {
      setLoading(false);
    }
  };

  const renderMetricsProgress = (metrics, title) => {
    if (!metrics || Object.keys(metrics).length === 0) return null;

    return (
      <div style={styles.card}>
        <div style={styles.cardContent}>
          <h3 style={{ 
            color: theme.colors.primary, 
            marginBottom: '16px',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {title}
          </h3>
          {Object.entries(metrics).map(([key, value]) => (
            <div key={key} style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '4px' 
              }}>
                <span style={{ 
                  color: theme.colors.textSecondary,
                  fontSize: '15px'
                }}>
                  {key.split('_').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </span>
                <span style={{ 
                  color: theme.colors.secondary,
                  fontWeight: '500',
                  fontSize: '15px'
                }}>
                  {Math.round(value * 100)}%
                </span>
              </div>
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill,
                    width: `${value * 100}%`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSummaryCard = (title, content) => {
    if (!content) return null;

    return (
      <div style={styles.card}>
        <div style={styles.cardContent}>
          <h3 style={{ 
            color: theme.colors.primary, 
            marginBottom: '16px',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {title}
          </h3>
          <p style={{ color: theme.colors.textSecondary, lineHeight: '1.6', fontSize: '16px' }}>
            {content}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: theme.colors.background 
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media (max-width: 768px) {
            .two-column-grid {
              grid-template-columns: 1fr !important;
            }
          }
          input:focus {
            outline: none;
            border-color: #02b1b2;
            box-shadow: 0 0 0 3px rgba(2, 177, 178, 0.1);
          }
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }
        `}
      </style>
      
      <div style={styles.header}>
        <div style={styles.container}>
          <div style={styles.headerContent}>
            <img 
              src="/sentiyo-logo.png" 
              alt="Sentiyo Logo" 
              style={styles.logo}
              onError={(e) => {
                // Fallback to text if logo fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h2 style={{ 
              color: theme.colors.primary, 
              margin: 0,
              fontSize: '24px',
              fontWeight: '600',
              display: 'none'
            }}>
              Sentiyo
            </h2>
            <button
              onClick={onLogout}
              style={{
                ...styles.button,
                backgroundColor: 'transparent',
                color: theme.colors.primary,
                borderColor: theme.colors.primary
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.cardContent}>
            <h2 style={{ 
              textAlign: 'center',
              color: theme.colors.primary,
              marginBottom: '8px',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              Audio Analysis
            </h2>
            <p style={{ 
              textAlign: 'center',
              color: theme.colors.textSecondary,
              marginBottom: '32px',
              fontSize: '16px'
            }}>
              Upload an audio file to analyze the conversation content and performance metrics.
            </p>
            
            <div>
              <input
                type="file"
                accept="audio/*"
                onChange={handleFileChange}
                style={styles.input}
              />
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    ...styles.button,
                    ...(loading ? styles.buttonDisabled : {})
                  }}
                >
                  {loading ? (
                    <div style={styles.loading}>
                      <div style={styles.spinner}></div>
                      Analyzing...
                    </div>
                  ) : "Analyze Audio"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ ...styles.alert, ...styles.alertError, marginTop: '24px' }}>
            {error}
          </div>
        )}

        {response && (
          <div style={{ marginTop: '32px' }}>
            {/* Issue Category */}
            {response.issue_category && (
              <div style={{ ...styles.card, marginBottom: '24px' }}>
                <div style={styles.cardContent}>
                  <h3 style={{ 
                    color: theme.colors.primary, 
                    marginBottom: '16px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Issue Category
                  </h3>
                  <span style={{
                    ...styles.chip,
                    backgroundColor: theme.colors.secondary,
                    color: 'white'
                  }}>
                    {response.issue_category}
                  </span>
                </div>
              </div>
            )}

            {/* Agent Performance and Customer Behavior in Same Row */}
            {(response.agent_performance_score || response.customer_behavior) && (
              <div 
                className="two-column-grid"
                style={{
                  display: 'grid',
                  gap: '24px',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  marginBottom: '24px'
                }}
              >
                {response.agent_performance_score && 
                  renderMetricsProgress(response.agent_performance_score, "Agent Performance Analysis")
                }
                {response.customer_behavior && 
                  renderMetricsProgress(response.customer_behavior, "Customer Behavior Analysis")
                }
              </div>
            )}

            {/* Summary Cards in Grid */}
            <div style={styles.grid}>
              {/* Agent Performance Summary */}
              {response.agent_performance_summary && 
                renderSummaryCard("Agent Performance Summary", response.agent_performance_summary)
              }

              {/* Customer Behavior Summary */}
              {response.customer_behavior_summary && 
                renderSummaryCard("Customer Behavior Summary", response.customer_behavior_summary)
              }
            </div>

            {/* Primary Complaint */}
            {response.primary_complaint && (
              <div style={{ ...styles.card, marginTop: '24px' }}>
                <div style={styles.cardContent}>
                  <h3 style={{ 
                    color: theme.colors.primary, 
                    marginBottom: '16px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Primary Complaint
                  </h3>
                  <div style={{ ...styles.alert, ...styles.alertError }}>
                    {response.primary_complaint}
                  </div>
                </div>
              </div>
            )}

            {/* Support Service Feedback */}
            {response.support_feedback && (
              <div style={{ ...styles.card, marginTop: '24px' }}>
                <div style={styles.cardContent}>
                  <h3 style={{ 
                    color: theme.colors.primary, 
                    marginBottom: '16px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Support Service Feedback
                  </h3>
                  <div style={{ ...styles.alert, ...styles.alertWarning }}>
                    {response.support_feedback}
                  </div>
                </div>
              </div>
            )}

            {/* Key Points */}
            {response.key_points && response.key_points.length > 0 && (
              <div style={{ ...styles.card, marginTop: '24px' }}>
                <div style={styles.cardContent}>
                  <h3 style={{ 
                    color: theme.colors.primary, 
                    marginBottom: '16px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Key Points
                  </h3>
                  <div>
                    {response.key_points.map((point, index) => (
                      <span
                        key={index}
                        style={{
                          ...styles.chip,
                          backgroundColor: theme.colors.primaryLight,
                          color: 'white',
                        }}
                      >
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Action Items */}
            {response.action_items && response.action_items.length > 0 && (
              <div style={{ ...styles.card, marginTop: '24px' }}>
                <div style={styles.cardContent}>
                  <h3 style={{ 
                    color: theme.colors.primary, 
                    marginBottom: '16px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    Action Items
                  </h3>
                  <div>
                    {response.action_items.map((item, index) => (
                      <span
                        key={index}
                        style={{
                          ...styles.chip,
                          backgroundColor: theme.colors.secondary,
                          color: 'white',
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Call Summary */}
            {response.summary && (
              <div style={{ marginTop: '24px' }}>
                {renderSummaryCard("Call Summary", response.summary)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const App = () => {
  const [token, setToken] = useState(null);
  const [currentView, setCurrentView] = useState('login');

  const handleLogin = (authToken) => {
    setToken(authToken);
    setCurrentView('upload');
  };

  const handleLogout = () => {
    setToken(null);
    setCurrentView('login');
  };

  if (currentView === 'login') {
    return <Login onLogin={handleLogin} />;
  }

  return <Upload token={token} onLogout={handleLogout} />;
};

export default App;